<template>
  <div>
    <PageHeader :title="t('title')" />
    <SearchToolbar
      class="mb-6"
      :placeholder="t('search_placeholder')"
      :initial-keywords="keywords"
      @update-keyword-search="updateKeywordSearch"
    />
    <Loading v-if="loading" />
    <div v-else-if="organizations.length === 0" class="text-center mt-4 py-2">
      <p>{{ t("no_results") }}</p>
    </div>
    <div v-else>
      <Table :columns="columns" :data="organizations">
        <template #invoiceLevel="{ row }">
          <div
            v-if="row.invoiceLevel"
            class="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium"
            :class="{
              'bg-blue-100 text-blue-800': row.invoiceLevel === 'organization',
              'bg-green-100 text-green-800': row.invoiceLevel === 'store',
            }"
          >
            {{ t(`invoice_level.${row.invoiceLevel}`) }}
          </div>
        </template>
        <template #source="{ row }">
          <div
            v-if="row.source"
            class="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium"
            :class="{
              'bg-blue-100 text-blue-800': row.source === 'web',
              'bg-green-100 text-green-800': row.source === 'retail',
              'bg-purple-100 text-purple-800': row.source === 'multichannel',
            }"
          >
            {{ t(`source_channel.${row.source}`) }}
          </div>
        </template>
      </Table>
      <Pagination
        :currentPage="currentPage"
        :totalPages="totalPages"
        :totalCount="totalCount"
        @changePage="handleChangePage"
      />
    </div>
  </div>
</template>

<script>
import { mapActions, mapState } from "vuex";
import { useI18n } from "vue-i18n";
import { PageHeader, Loading, Table, Pagination, SearchToolbar } from "@estaly/ui";

export default {
  components: { PageHeader, Loading, Table, Pagination, SearchToolbar },

  setup() {
    const { t } = useI18n();
    return { t };
  },

  data() {
    return {
      loading: true,
      keywords: null,
      columns: [],
    };
  },

  computed: {
    ...mapState("organizations", {
      organizations: "index",
      currentPage: "currentPage",
      totalPages: "totalPages",
      totalCount: "totalCount",
    }),
  },

  async created() {
    this.buildColumns();
    await this.retrieveOrganizations();
  },

  methods: {
    ...mapActions("organizations", { fetchOrganizations: "fetchIndex" }),

    buildColumns() {
      this.columns = [
        { label: this.t("column_id"), field: "id", widthClass: "w-16" },
        { label: this.t("column_name"), field: "name", bold: true },
        { label: this.t("column_business_name"), field: "businessName" },
        { label: this.t("column_invoice_level"), field: "invoiceLevel", slot: "invoiceLevel" },
        { label: this.t("column_sales_channel"), field: "source", slot: "source" },
      ];
    },

    async retrieveOrganizations(page = 1) {
      this.loading = true;
      let response;
      try {
        response = await this.fetchOrganizations({ page, keywords: this.keywords });
      } catch (error) {
        console.error(error);
      } finally {
        // An outdated response must not stop the loader of the request still pending.
        if (response?.isLatest !== false) this.loading = false;
      }
    },

    handleChangePage(page) {
      this.retrieveOrganizations(page);
    },

    updateKeywordSearch(keywords) {
      this.keywords = keywords;
      this.retrieveOrganizations();
    },
  },
};
</script>

<i18n src="./OrganizationList.i18n.json"></i18n>
